import {
  CPropertyBuildingUnitForm,
  IPropertyBuildingUnitForm
} from "@rf/property/module/components/property-form/util/schemas/building-form/building-unit.schema";

import { IPropertyForm } from "@rf/property/module/components/property-form/util/schemas/property-schema/propertySchema";

import { CPropertyFormKeys } from "@rf/property/module/components/property-form/util/schemas/property-schema/propertySchema";

import { FieldValues, useFieldArray, UseFormReturn } from "react-hook-form";

import useAlert from "@rf-hooks/useAlert";
import {
  CPropertyBuildingForm,
  IPropertyBuildingForm,
  propertyBuildingFormSchema
} from "@rf/property/module/components/property-form/util/schemas/building-form/building.schema";

type UseBuildingsHelpProps = UseFormReturn<FieldValues, any, undefined>;
const useBuildingsHelp = ({
  control,
  getValues,
  setValue
}: UseBuildingsHelpProps) => {
  const { alert } = useAlert();
  const { fields, append, remove, update, insert, replace } = useFieldArray({
    control,
    name: CPropertyFormKeys.building
  });

  const getNewUnit = () => {
    const formValues = getValues() as IPropertyForm;
    return {
      ...CPropertyBuildingUnitForm,
      id: crypto.randomUUID(),
      isNew: true,
      price: formValues.price_sale_property,
      currency: formValues.currency_sale_property,
      metersLand: formValues.meter_land_property,
      metersConstruction: formValues.meter_construction_property,
      commissionGuaranteed: formValues.fee_sale_selling_agent_commission_percentage,
      totalCommission: formValues.fee_sale_percentage_commission_property,
      uuid: crypto.randomUUID()
    };
  };

  const addBuilding = async (): Promise<boolean> => {
    const formValues = getValues() as IPropertyForm;

    try {
      const newBuilding = {
        ...CPropertyBuildingForm,
        id: crypto.randomUUID(),
        uuid: crypto.randomUUID(),
        name: "",
        units: [getNewUnit()],
        isCaptured: formValues.building?.length === 0
      };

      append(newBuilding);
      return true;
    } catch (error) {
      return false;
    }
  };

  const deleteBuilding = async (index: number): Promise<boolean> => {
    try {
      const buildings = getValues(CPropertyFormKeys.building);

      if (
        buildings.filter((b: IPropertyBuildingForm) => b.isCaptured).length === 1 &&
        buildings[index].isCaptured
      ) {
        await alert({
          title: "Aviso",
          type: "warning",
          message: "No se puede eliminar el único edificio captado",
          color: "warning.main",
          confirmBtnMessage: "Ok"
        });
        return false;
      }

      if (buildings.length <= 1) {
        await alert({
          title: "Aviso",
          type: "info",
          message: "No se puede eliminar el último edificio",
          color: "warning.main",
          confirmBtnMessage: "Ok"
        });
        return false;
      }

      const buildingToDelete = buildings[index] as IPropertyBuildingForm & {
        uuid: string;
        id: string;
        isNew?: boolean;
      };

      if (buildingToDelete.isNew) {
        remove(index);
        return true;
      }

      const currentBuildingsDeleted = getValues("buildingsDeleted") || [];
      setValue("buildingsDeleted", [
        ...currentBuildingsDeleted,
        buildingToDelete.id
      ]);
      remove(index);
      return true;
    } catch (error) {
      return false;
    }
  };

  const duplicateBuilding = async (index: number, times = 1): Promise<boolean> => {
    try {
      const buildings = getValues(CPropertyFormKeys.building);
      if (times > 10) {
        await alert({
          title: "Límite excedido",
          type: "warning",
          message: "El máximo de edificios a duplicar es 10",
          color: "warning.main",
          confirmBtnMessage: "Entendido"
        });
        return false;
      }

      const buildingToDuplicate = buildings[index] as IPropertyBuildingForm & {
        uuid: string;
        id: string;
      };

      if (!buildingToDuplicate) {
        return false;
      }

      try {
        await propertyBuildingFormSchema.validate(buildingToDuplicate, {
          abortEarly: false
        });
      } catch (error) {
        await alert({
          title: "Advertencia",
          type: "warning",
          message:
            "Para duplicar la etapa, primero complete la información de sus unidades. Vaya a la sección 'Etapas y unidades' para agregar los datos necesarios.",
          color: "warning.main",
          confirmBtnMessage: "Entendido"
        });
        return false;
      }

      for (let i = 0; i < times; i++) {
        const units: IPropertyBuildingUnitForm[] =
          buildingToDuplicate.units?.map(unit => ({
            ...unit,
            id: crypto.randomUUID(),
            uuid: crypto.randomUUID(),
            isNew: true
          })) || [];

        const baseName = buildingToDuplicate.name;
        const duplicatedBuilding = {
          ...buildingToDuplicate,
          id: crypto.randomUUID(),
          uuid: crypto.randomUUID(),
          isNew: true,
          name: `${baseName}`,
          units
        };
        insert(index + 1 + i, duplicatedBuilding);
      }
      return true;
    } catch (error) {
      return false;
    }
  };

  const getUpBuildingFromTemplate = (template: any) => {
    replace([]);
    setTimeout(() => {
      replace(template);
    }, 500);
  };

  return {
    addBuilding,
    deleteBuilding,
    duplicateBuilding,
    fields,
    append,
    remove,
    update,
    getUpBuildingFromTemplate
  };
};

export default useBuildingsHelp;
